import { useEffect, useState, useCallback } from 'react'
import { apiGet, apiPatch } from '../api'

interface UrgentTask {
  id: string
  title: string
  repo_label: string
  source: string
  source_id: string | null
  importance: string | null
  due_at: number | null
  detected_at: number
  // Joined inbox context (only set when source='inbox')
  source_from: string | null
  source_subject: string | null
  source_snippet: string | null
  source_body_preview: string | null
  source_body: string | null
  source_received_at: number | null
  source_account: string | null
}

function fmtDue(ts: number | null): string | null {
  if (!ts) return null
  const days = (ts - Date.now()) / (24 * 3600 * 1000)
  if (days < 0) return `overdue ${Math.abs(Math.round(days))}d`
  if (days < 1) return 'today'
  if (days < 2) return 'tomorrow'
  return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

function sourceTag(source: string, repoLabel: string): { label: string; tint: string } {
  if (source === 'inbox')  return { label: 'inbox', tint: 'rgba(255,107,129,0.16)' }
  if (source === 'mstodo') return { label: 'mstodo', tint: 'rgba(125,211,252,0.16)' }
  if (repoLabel) return { label: repoLabel, tint: 'rgba(154,169,191,0.10)' }
  return { label: source, tint: 'rgba(154,169,191,0.10)' }
}

function inboxThreadLink(t: UrgentTask): string | null {
  // tasks from inbox have id like "inbox-task:<email_id>:<idx>"
  if (t.source !== 'inbox') return null
  const m = t.id.match(/^inbox-task:(.+):(\d+)$/)
  if (!m) return null
  const emailId = m[1]
  if (emailId?.startsWith('ms:')) return 'https://outlook.office.com/mail/inbox'
  return `https://mail.google.com/mail/u/0/#inbox/${emailId}`
}

export default function UrgentTasks() {
  const [tasks, setTasks] = useState<UrgentTask[] | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const r = await apiGet<{ tasks: UrgentTask[] }>('/tasks?status=open&importance=high')
      setTasks(r.tasks)
    } catch { /* ignore */ }
  }, [])

  function fmtAge(ms: number | null): string {
    if (!ms) return ''
    const d = new Date(ms)
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  }
  function senderName(addr: string | null): string {
    if (!addr) return ''
    const m = addr.match(/^"?([^"<]+?)"?\s*<.+?>$/)
    return m?.[1]?.trim() ?? addr
  }
  function replyWithMercedes(t: UrgentTask) {
    const sender = senderName(t.source_from)
    const body = t.source_body || t.source_body_preview || t.source_snippet || ''
    const prompt = [
      `Task: ${t.title}`,
      `Draft a reply to ${sender || 'the sender'}.`,
      t.source_subject ? `Subject: ${t.source_subject}` : '',
      `Their message:`,
      `> ${body.slice(0, 1000)}`,
      ``,
      `My reply:`,
    ].filter(Boolean).join('\n')
    window.dispatchEvent(new CustomEvent('mercedes:open', { detail: { prompt } }))
  }

  useEffect(() => {
    load()
    const t = setInterval(load, 60_000)
    return () => clearInterval(t)
  }, [load])

  async function close(id: string) {
    setTasks(prev => prev?.filter(t => t.id !== id) ?? null)
    try { await apiPatch(`/tasks/${id}`, { status: 'done' }) } catch { load() }
  }

  return (
    <section className="panel work-panel" style={{ borderColor: 'rgba(255,107,129,0.32)' }}>
      <div className="panel-header">
        <span style={{ color: 'var(--critical)' }}>🔥 Urgent</span>
        <span className="mono muted">
          {tasks ? `${tasks.length} high-priority` : '…'}
        </span>
      </div>
      {!tasks && <div className="empty-state">Loading…</div>}
      {tasks && tasks.length === 0 && (
        <div className="empty-state" style={{ color: 'var(--ok)' }}>
          ✓ Nothing urgent. You're on top of things.
        </div>
      )}
      {tasks && tasks.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0,
                     display: 'flex', flexDirection: 'column', gap: 4,
                     maxHeight: 480, overflowY: 'auto' }}>
          {tasks.map(t => {
            const tag = sourceTag(t.source ?? '', t.repo_label)
            const due = fmtDue(t.due_at)
            const overdue = t.due_at && t.due_at < Date.now()
            const link = inboxThreadLink(t)
            const isOpen = expanded === t.id
            const hasContext = t.source === 'inbox' && (t.source_from || t.source_body || t.source_body_preview || t.source_snippet)
            return (
              <li key={t.id} style={{
                borderRadius: 6,
                background: 'rgba(255,107,129,0.04)',
                border: '1px solid rgba(255,107,129,0.14)',
                fontSize: '0.82rem',
                overflow: 'hidden',
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, padding: '6px 8px' }}>
                  <input type="checkbox" onChange={() => close(t.id)}
                    style={{ accentColor: 'var(--critical)', flexShrink: 0 }}
                    aria-label="Mark done" />
                  <span style={{
                    flex: '0 0 auto', padding: '1px 6px', borderRadius: 999,
                    fontFamily: 'var(--font-mono)', fontSize: '0.6rem',
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    background: tag.tint, color: 'var(--text-secondary)',
                    border: '1px solid rgba(154,169,191,0.18)',
                  }}>{tag.label}</span>
                  <button type="button"
                    onClick={() => setExpanded(isOpen ? null : t.id)}
                    aria-label={isOpen ? 'Collapse' : 'Expand context'}
                    style={{ flex: 1, minWidth: 0, textAlign: 'left',
                             background: 'transparent', border: 'none', padding: 0,
                             cursor: 'pointer', color: 'var(--text-primary)',
                             font: 'inherit', display: 'flex', gap: 6, alignItems: 'baseline' }}>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden',
                                   textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.title}
                    </span>
                    {hasContext && (
                      <span style={{ flexShrink: 0, color: 'var(--text-muted)',
                                     fontSize: '0.65rem' }}>{isOpen ? '▴' : '▾'}</span>
                    )}
                  </button>
                  {due && (
                    <span className="mono" style={{
                      flexShrink: 0, fontSize: '0.65rem',
                      color: overdue ? 'var(--critical)' : 'var(--text-muted)',
                      fontWeight: overdue ? 600 : 400,
                    }}>{due}</span>
                  )}
                </div>
                {isOpen && hasContext && (
                  <div style={{
                    padding: '8px 10px 10px 38px',
                    borderTop: '1px solid rgba(255,107,129,0.10)',
                    background: 'rgba(0,0,0,0.18)',
                    fontSize: '0.78rem',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                                  alignItems: 'baseline', gap: 8, marginBottom: 6,
                                  flexWrap: 'wrap' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>
                        <span className="mono muted" style={{ fontSize: '0.62rem',
                                                              letterSpacing: '0.1em',
                                                              textTransform: 'uppercase' }}>
                          From
                        </span>{' '}
                        {senderName(t.source_from) || t.source_from}
                      </span>
                      {t.source_received_at && (
                        <span className="mono muted" style={{ fontSize: '0.65rem' }}>
                          {fmtAge(t.source_received_at)}
                        </span>
                      )}
                    </div>
                    {t.source_subject && (
                      <div style={{ marginBottom: 8, color: 'var(--text-secondary)' }}>
                        <span className="mono muted" style={{ fontSize: '0.62rem',
                                                              letterSpacing: '0.1em',
                                                              textTransform: 'uppercase' }}>
                          Subject
                        </span>{' '}
                        {t.source_subject}
                      </div>
                    )}
                    <pre style={{
                      margin: 0, fontFamily: 'inherit', fontSize: '0.78rem',
                      lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      color: 'var(--text-primary)',
                      maxHeight: 240, overflowY: 'auto',
                      background: 'rgba(0,0,0,0.25)', padding: 8, borderRadius: 4,
                    }}>
                      {t.source_body || t.source_body_preview || t.source_snippet || '(no body stored)'}
                    </pre>
                    <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => replyWithMercedes(t)}
                        style={{ minHeight: 26, padding: '0 10px', fontSize: '0.7rem',
                                 background: 'rgba(90,242,255,0.10)',
                                 borderColor: 'rgba(90,242,255,0.4)',
                                 color: 'var(--accent)' }}>
                        ↩ reply with Mercedes
                      </button>
                      {link && (
                        <a href={link} target="_blank" rel="noreferrer"
                          style={{ minHeight: 26, padding: '4px 10px', fontSize: '0.7rem',
                                   background: 'rgba(255,255,255,0.03)',
                                   border: '1px solid var(--border-soft)',
                                   color: 'var(--text-secondary)',
                                   borderRadius: 999, textDecoration: 'none' }}>
                          open in {t.source_account?.startsWith('ms:') ? 'Outlook' : 'Gmail'} ↗
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
